import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { CredentialsService } from '@app/core/credentials.service';
import { HelperService } from '@app/core/helper.service';
import { Logger } from '@app/core/logger.service';
import { GraphQLResponse } from '@app/models';

const log = new Logger('ErrorHandlerInterceptor');

/**
 * Adds a default error handler to all requests.
 */
@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerInterceptor implements HttpInterceptor {
  private lastReloadTime = 0;
  private recentReloadCount = 0;

  constructor(
    private router: Router,
    private credentialsService: CredentialsService,
    private helperService: HelperService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(catchError(error => this.errorHandler(error)));
  }

  private errorHandler(errorData: HttpErrorResponse): Observable<HttpEvent<any>> {
    if (!errorData || ![404].includes(errorData.status)) {
      log.debug('Request error', this.router.url, errorData);
    }

    if (errorData) {
      if ([401, 429].includes(errorData.status)) {
        // Can't run the logout request since it would be looping HttpClient. But it's not necessary anyway, so it just clears the credentials
        this.credentialsService.clearCredentials();

        if (this.helperService.isDocumentPage()) {
          if (!this.reload()) {
            this.router.navigate(['/entrar'], { replaceUrl: true });
          }
        } else if (!/^\/(401|404|423|503)$/.test(this.router.url)) {
          this.router.navigate(['/entrar'], { replaceUrl: true });
        }
      } else if (errorData.status === 423) {
        this.credentialsService.clearCredentials();
        this.router.navigate(['/423'], { replaceUrl: true });
      } else if (errorData.status === 503) {
        this.router.navigate(['/503'], { replaceUrl: true });
      }
    }

    if (errorData && [423, 503].includes(errorData.status)) {
      return EMPTY;
    } else {
      // Throws error with similar structure as the success request, to use ErrorHandlerService
      throw { data: null, errors: errorData ? [errorData.error] : [], status: errorData ? errorData.status : -1 } as GraphQLResponse;
    }
  }

  private reload() {
    const currentUrl = this.router.url;
    const now = Date.now();

    // Can only reload a maximum of 5 times in 10 seconds to avoid infinite loop
    if (now - this.lastReloadTime > 10000) {
      this.recentReloadCount = 0;
      this.lastReloadTime = now;
    }
    if (this.recentReloadCount >= 5) {
      return false;
    }

    this.recentReloadCount++;
    this.lastReloadTime = now;

    // Reloads component, instead of full reload like location.reload()
    this.router.navigateByUrl('/404', { skipLocationChange: true }).then(() => this.router.navigate([currentUrl]));
    return true;
  }
}
